import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { http } from "../lib/http";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Textarea } from "./ui/textarea";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { AlertCircle, Send } from "lucide-react";
import { toast } from "sonner";

const REASONS = ["illness", "family", "medical_appointment", "other"];

export default function AbsenceNotifyCard() {
  const { t } = useTranslation();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [dates, setDates] = useState("");
  const [reason, setReason] = useState("illness");
  const [detail, setDetail] = useState("");
  const [recent, setRecent] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const fetchData = async () => {
    try {
      const [s, n] = await Promise.all([
        http.get("/students?limit=20"),
        http.get(`/absence-notices?from=${new Date(Date.now() - 90 * 86400000).toISOString().slice(0,10)}`),
      ]);
      setStudents(s.data.items || []);
      if (!studentId && (s.data.items || []).length > 0) setStudentId(s.data.items[0].id);
      setRecent(n.data.items || []);
    } catch { /* not a parent */ }
  };
  useEffect(() => { fetchData(); }, []); // eslint-disable-line

  const submit = async () => {
    const dateList = dates.split(",").map(d => d.trim()).filter(Boolean);
    if (!studentId || !dateList.length) {
      toast.error("Pick a student and at least one date");
      return;
    }
    setSubmitting(true);
    try {
      await http.post("/absence-notices", {
        student_id: studentId, dates: dateList, reason, reason_detail: detail,
      });
      toast.success(`${dateList.length} notice(s) submitted`);
      setDates(""); setDetail("");
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submit failed");
    } finally { setSubmitting(false); }
  };

  if (!students.length) return null;

  return (
    <Card className="bg-white p-5 border border-slate-200" data-testid="absence-notify-card">
      <div className="flex items-center gap-2 mb-3">
        <AlertCircle className="h-5 w-5 text-[#C8102E]"/>
        <h3 className="font-serif text-lg text-[#0A1F44]" style={{ fontFamily: "'Segoe UI', 'Selawik', Tahoma, Geneva, Verdana, system-ui, sans-serif" }}>
          Notify an upcoming absence
        </h3>
      </div>
      <p className="text-xs text-slate-500 mb-3">Heads-up the school in advance — your child's status will be auto-justified when the register is saved.</p>
      <div className="grid gap-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs">Student</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="absence-form-student"><SelectValue/></SelectTrigger>
              <SelectContent>
                {students.map(s => <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Reason</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="absence-form-reason"><SelectValue/></SelectTrigger>
              <SelectContent>
                {REASONS.map(r => <SelectItem key={r} value={r}>{r.replace("_"," ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div>
          <Label className="text-xs">Dates (comma-separated, YYYY-MM-DD)</Label>
          <Input placeholder="2026-05-12, 2026-05-13" value={dates} onChange={e => setDates(e.target.value)} data-testid="absence-form-dates"/>
        </div>
        <div>
          <Label className="text-xs">Details (optional)</Label>
          <Textarea rows={2} value={detail} onChange={e => setDetail(e.target.value)} placeholder="Dentist appointment, family travel…" data-testid="absence-form-detail"/>
        </div>
        <Button onClick={submit} disabled={submitting} className="bg-[#0A1F44] self-start" data-testid="absence-form-submit">
          <Send className="h-3.5 w-3.5 mr-2"/>{submitting ? "..." : "Submit notice"}
        </Button>
      </div>

      {recent.length > 0 && (
        <div className="mt-5 pt-4 border-t border-slate-100">
          <h4 className="text-xs uppercase tracking-wider text-slate-500 mb-2">Recent notices</h4>
          <ul className="space-y-1.5 text-sm">
            {recent.slice(0, 8).map(n => (
              <li key={n.id} className="flex items-center justify-between gap-2">
                <span><b>{n.date}</b> · {n.reason}{n.reason_detail ? ` — ${n.reason_detail}` : ""}</span>
                <Badge variant={n.status === "pending" ? "secondary" : "default"}
                       className={n.status === "acknowledged" ? "bg-emerald-600" : ""}>{n.status}</Badge>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
